import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RegulationStatus } from '@vendasta/protos/sms/v2/enums';
import type { PhoneProvisioningInfo, RegulationInfo } from '@vendasta/protos/sms/v2/types';
import { combineLatest, distinctUntilChanged, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { OwnerService } from '../../../common/owner.service';
import { PhoneProvisioningService } from '../../phone-provisioning/phone-provisioning.service';
import { ProgressTrackerComponent, StageStatus, Status } from '../../progress-tracker/progress-tracker.component';
import { countryFromCode, hasNumberProvisioningStep } from '../../../constants';
import {
  getPhoneProvisioningMessageKey,
  getPhoneProvisioningStageStatus,
} from '../phone-provisioning-helper-functions';
import { RegulationService } from '../regulation.service';

const STAGES_KEY = 'SMS_REGULATION.COMMON.STATUS.STAGES';
const RESOURCE_TYPE_KEY = 'SMS_REGULATION.COMMON.STATUS.RESOURCE_TYPE';
const TWILIO_ERROR_KEY = 'SMS_REGULATION.COMMON.STATUS.TWILIO_ERROR';
const APPROVED_MESSAGE_KEY = 'SMS_REGULATION.COMMON.STATUS.APPROVED_MESSAGE';

@Component({
  selector: 'sms-regulatory-status',
  imports: [AsyncPipe, MatCardModule, GalaxyAlertModule, ProgressTrackerComponent, TranslateModule],
  templateUrl: './sms-regulatory-status.component.html',
  styleUrl: './sms-regulatory-status.component.scss',
})
export class SmsRegulatoryStatusComponent {
  private readonly regulationService = inject(RegulationService);
  private readonly phoneProvisioningService = inject(PhoneProvisioningService);
  private readonly ownerService = inject(OwnerService);
  private readonly translate = inject(TranslateService);

  protected readonly regulationInfo$: Observable<RegulationInfo | null> = this.regulationService.regulationInfo$;

  private readonly hasProvisioningStep$: Observable<boolean> = this.ownerService.getOwnerLocation$().pipe(
    map((location) => hasNumberProvisioningStep(countryFromCode(location.country))),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Only read phone-provisioning once the regulation is approved (avoids needless calls). */
  private readonly provisioningInfo$: Observable<PhoneProvisioningInfo | null> =
    this.regulationService.regulationStatus$.pipe(
      switchMap((status) => {
        const isRegulationStatusApproved = status === RegulationStatus.REGULATION_STATUS_APPROVED;
        if (!isRegulationStatusApproved) {
          return of(null);
        }
        return this.phoneProvisioningService.phoneProvisioningInfo$;
      }),
    );

  protected readonly stages$: Observable<StageStatus[]> = combineLatest([
    this.regulationInfo$,
    this.hasProvisioningStep$,
    this.provisioningInfo$,
  ]).pipe(
    map(([info, hasProvisioningStep, provisioning]) => this.buildStages(info, hasProvisioningStep, provisioning)),
  );

  protected readonly approvedStatusMessage$: Observable<string> = combineLatest([
    this.hasProvisioningStep$,
    this.provisioningInfo$,
  ]).pipe(
    map(([hasProvisioningStep, provisioning]) => this.buildApprovedStatusMessage(hasProvisioningStep, provisioning)),
  );

  /** Provisioning failure reason — surfaced as a highlighted alert on the 4th stage only */
  protected readonly provisioningFailureReason$: Observable<string | null> = combineLatest([
    this.hasProvisioningStep$,
    this.provisioningInfo$,
  ]).pipe(
    map(([hasProvisioningStep, provisioning]) => {
      if (!hasProvisioningStep) {
        return null;
      }
      const stageStatus = getPhoneProvisioningStageStatus(provisioning, RegulationStatus.REGULATION_STATUS_APPROVED);
      if (stageStatus !== Status.Failed) {
        return null;
      }
      return provisioning?.requestFailureReason?.trim() || null;
    }),
  );

  protected readonly RegulationStatus = RegulationStatus;

  protected resourceTypeLabel(resourceType: string): string {
    if (!resourceType) {
      return this.translate.instant(`${RESOURCE_TYPE_KEY}.UNKNOWN`);
    }
    const key = `${RESOURCE_TYPE_KEY}.${resourceType.toUpperCase()}`;
    const translated = this.translate.instant(key);
    return translated === key ? resourceType : translated;
  }

  protected rejectionMessage(errorCodes: readonly number[] | undefined, fallback: string): string {
    if (!errorCodes?.length) {
      return fallback;
    }
    const translated = errorCodes
      .map((code) => {
        const key = `${TWILIO_ERROR_KEY}.${code}`;
        const value = this.translate.instant(key);
        return value === key ? null : value;
      })
      .filter((s): s is string => s !== null);
    return translated.length > 0 ? translated.join(' ') : fallback;
  }

  private stageLabel(key: string): string {
    return this.translate.instant(`${STAGES_KEY}.${key}`);
  }

  private buildApprovedStatusMessage(hasProvisioningStep: boolean, provisioning: PhoneProvisioningInfo | null): string {
    if (!hasProvisioningStep) {
      return this.translate.instant(APPROVED_MESSAGE_KEY);
    }
    const stageStatus = getPhoneProvisioningStageStatus(provisioning, RegulationStatus.REGULATION_STATUS_APPROVED);
    const messageKey = getPhoneProvisioningMessageKey(stageStatus) ?? APPROVED_MESSAGE_KEY;
    return this.translate.instant(messageKey);
  }

  private buildStages(
    info: RegulationInfo | null,
    hasProvisioningStep: boolean,
    provisioning: PhoneProvisioningInfo | null,
  ): StageStatus[] {
    const stages = this.buildRegulationStages(info);
    if (!hasProvisioningStep) {
      return stages;
    }
    // Provisioning countries: stage 3 is relabeled "Business information", plus a 4th provisioning stage.
    stages[2] = { name: this.stageLabel('BUSINESS_INFORMATION'), status: stages[2].status };
    const regulationStatus = info?.status ?? RegulationStatus.REGULATION_STATUS_UNSPECIFIED;
    stages.push({
      name: this.stageLabel('PHONE_NUMBER_PROVISIONING'),
      status: getPhoneProvisioningStageStatus(provisioning, regulationStatus),
    });
    return stages;
  }

  private buildRegulationStages(info: RegulationInfo | null): StageStatus[] {
    if (!info) {
      return [
        { name: this.stageLabel('SUBMIT_INFORMATION'), status: Status.NotStarted },
        { name: this.stageLabel('REVIEW'), status: Status.NotStarted },
        { name: this.stageLabel('APPROVED'), status: Status.NotStarted },
      ];
    }

    switch (info.status) {
      case RegulationStatus.REGULATION_STATUS_APPROVED:
        return [
          { name: this.stageLabel('INFORMATION_SUBMITTED'), status: Status.Completed },
          { name: this.stageLabel('REVIEW'), status: Status.Completed },
          { name: this.stageLabel('APPROVED'), status: Status.Completed },
        ];
      case RegulationStatus.REGULATION_STATUS_REJECTED:
        return [
          { name: this.stageLabel('INFORMATION_SUBMITTED'), status: Status.Completed },
          { name: this.stageLabel('REVIEW'), status: Status.Failed },
          { name: this.stageLabel('REJECTED'), status: Status.Failed },
        ];
      case RegulationStatus.REGULATION_STATUS_PENDING_REVIEW:
      default:
        return [
          { name: this.stageLabel('INFORMATION_SUBMITTED'), status: Status.Completed },
          { name: this.stageLabel('REVIEW'), status: Status.InProgress },
          { name: this.stageLabel('APPROVED'), status: Status.NotStarted },
        ];
    }
  }
}
